<template>
  <div class="relative w-full">
    <div>
      <Listbox
        v-slot="{ open }"
        v-model="selected"
        as="div"
        class="relative w-full"
      >
        <ListboxButton
          class="relative flex w-full cursor-pointer appearance-none items-center justify-between border-0 border-b-2 bg-transparent px-0 py-2.5 text-sm text-primary focus:border-primary focus:outline-none focus:ring-0"
          :class="[
            open ? 'border-primaryLight' : 'border-darkestGray',
            hasError ? 'border-error' : 'border-darkestGray',
          ]"
        >
          <div class="flex items-center">
            <slot name="selected" :selected="selectedOption">
              <span class="font-semibold text-primary">
                {{ selectedOption ? selectedOption.name : $t(placeholder) }}
              </span>
            </slot>
          </div>

          <inline-svg
            :src="useAsset(`icons/base/angle-down.svg`)"
            class="transform"
            :class="open ? 'rotate-180' : 'rotate-0'"
            width="13"
            height="8"
            loading="lazy"
            alt="angle-down-icon"
          />
        </ListboxButton>

        <transition
          leave-active-class="transition ease-in duration-100"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <ListboxOptions
            class="pointer-events-auto absolute z-[100] max-h-96 w-full overflow-auto rounded-md bg-white shadow-md focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
          >
            <ListboxOption
              v-for="option in options"
              :key="option.value"
              v-slot="{ selected }"
              as="template"
              :value="option.value"
            >
              <li
                class="transition-ease-in-out relative flex cursor-pointer items-center px-4 py-1 py-3 text-sm text-primary hover:bg-[#F5F7FA] md:text-base"
                :class="{ 'bg-[#F5F7FA]': selected }"
              >
                <div class="flex items-center">
                  <slot name="option" v-bind="option">
                    {{ option.name }}
                  </slot>
                </div>
              </li>
            </ListboxOption>
          </ListboxOptions>
        </transition>
      </Listbox>
    </div>
    <label
      v-if="label"
      class="absolute top-3 origin-[0] -translate-y-6 scale-100 transform text-sm font-medium uppercase tracking-wider text-darkestGray duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-6 peer-focus:scale-100 peer-focus:text-primary"
    >
      {{ label }}
      <span v-if="isRequired" class="!text-[#eb5757]">*</span>
    </label>
    <BaseErrorMessage v-if="hasError">
      <div class="inline-flex items-center space-x-2">
        <inline-svg
          :src="useAsset(`icons/base/error.svg`)"
          loading="lazy"
          alt="error-icon"
        />
        <span>
          {{ $t(errorMsg) }}
        </span>
      </div>
    </BaseErrorMessage>
  </div>
</template>

<script setup>
import InlineSvg from 'vue-inline-svg'

import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/vue'

const emit = defineEmits(['update:modelValue'])

const props = defineProps({
  modelValue: {
    type: [String, Number, null],
    default: null,
  },
  options: {
    type: [Array],
    default: () => [],
  },
  name: {
    type: [String],
    default: '',
  },
  label: {
    type: [String],
    default: null,
  },
  placeholder: {
    type: [String],
    default: 'action.choose',
  },
  isRequired: {
    type: [Boolean],
    default: false,
  },
  anyDirty: {
    type: [Boolean],
    default: true,
  },
  errors: {
    type: [Object],
    default: null,
  },
})

const selectedOption = computed(() => {
  return props.options.find((option) => option.value === selected.value)
})

const hasError = computed(() => {
  return props.anyDirty && props.errors && props.errors?.[props.name]
})

const errorMsg = computed(() => {
  return props.errors[props.name] instanceof Array
    ? props.errors[props.name][0]
    : props.errors[props.name]
})

const selected = computed({
  get() {
    return props.modelValue
  },
  set(value) {
    emit('update:modelValue', value)
  },
})
</script>

<style scoped>
ul::-webkit-scrollbar {
  @apply w-1.5;
}

ul::-webkit-scrollbar-track {
  @apply rounded-md bg-[#F5F7FA];
}

ul::-webkit-scrollbar-thumb,
ul::-webkit-scrollbar-thumb:hover {
  @apply rounded-md bg-darkGray;
}
</style>
